// MACHINERY V2 (Stroje) — SR detail: the metadata-column atoms (M2.6).
//
// Split out of SrDetailMeta.tsx for the 300-line cap (that file hit 303 with
// them inline). SrDetailMeta owns WHICH rows exist and what they bind; this
// owns what a card, a meta row and a person row look like.
//
// Reference impls: design_handoff_machinery_v3/machinery-sr.jsx `SrCard`
// (lines 158-181), `SrMetaRow` (:454-466) and `SrPersonRow` (:207-237);
// artboards 0700 / 0701.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { formatTabDate } from "../profile/tabs/tabFormat";

export const MetaCard: React.FC<{ eyebrow: string; children: React.ReactNode }> = ({ eyebrow, children }) => (
  <section
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 12,
      boxShadow: "var(--shadow)",
      padding: "16px 18px",
      minWidth: 0,
    }}
  >
    <div className="eyebrow" style={{ color: "var(--mod-accent)", marginBottom: 10 }}>
      {eyebrow}
    </div>
    {children}
  </section>
);

export const MetaRow: React.FC<{
  glyph: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ glyph, label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
      <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>{glyph}</span>
      <span className="eyebrow" style={{ color: "var(--faint)" }}>
        {label}
      </span>
    </div>
    <div
      style={{
        fontSize: 13.5,
        color: "var(--ink)",
        fontWeight: 500,
        paddingLeft: 18,
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {children}
    </div>
  </div>
);

const initials = (name: string): string =>
  name
    .split(" ")
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .substring(0, 2);

export const PersonRow: React.FC<{
  name: string;
  role?: string;
  at?: Date;
  token: string;
}> = ({ name, role, at, token }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderLeft: `2px solid var(${token})`,
      background: "var(--bg-soft)",
      borderRadius: 6,
      minWidth: 0,
    }}
  >
    <span
      aria-hidden
      style={{
        width: 32,
        height: 32,
        borderRadius: 999,
        flexShrink: 0,
        background: `color-mix(in oklab, var(${token}) 22%, var(--card))`,
        border: `1px solid color-mix(in oklab, var(${token}) 32%, var(--border))`,
        color: `var(${token})`,
        display: "grid",
        placeItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {initials(name)}
    </span>
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{name}</span>
        {role && (
          <span
            style={{
              fontSize: 10.5,
              padding: "1px 6px",
              borderRadius: 4,
              background: "var(--card)",
              color: "var(--muted)",
              border: "1px solid var(--border)",
              whiteSpace: "nowrap",
            }}
          >
            {role}
          </span>
        )}
      </div>
      {at && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            color: "var(--faint)",
            marginTop: 2,
          }}
        >
          {formatTabDate(at)}
        </span>
      )}
    </div>
  </div>
);

export const PeopleLabel: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 10,
      color: "var(--faint)",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      marginBottom: 4,
    }}
  >
    {text}
  </div>
);
